export const YOUTUBE_VIDEO_EMBED_ERROR = "Csak érvényes YouTube videó linket lehet beágyazni.";

const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

export type YouTubeVideoEmbed = {
  provider: "youtube";
  videoId: string;
  url: string;
  embedUrl: string;
};

function cleanVideoId(value: string | null | undefined) {
  const videoId = (value ?? "").trim();
  return YOUTUBE_VIDEO_ID_PATTERN.test(videoId) ? videoId : null;
}

function decodeHtmlEntities(input: string) {
  return input
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");
}

function extractIframeSrc(input: string) {
  if (!/<iframe[\s>]/i.test(input)) return null;
  const match = input.match(/<iframe\b[^>]*\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
  return match ? decodeHtmlEntities(match[1] ?? match[2] ?? match[3] ?? "").trim() : "";
}

function appendSupportedYouTubeParams(embedUrl: URL, sourceUrl: URL) {
  for (const key of ["start", "t", "si", "list", "playlist"]) {
    const value = sourceUrl.searchParams.get(key);
    if (value) embedUrl.searchParams.set(key, value);
  }
}

export function youtubeEmbedUrl(videoId: string, source?: string) {
  const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`);
  if (source) {
    try {
      appendSupportedYouTubeParams(embedUrl, new URL(source));
    } catch {}
  }
  return embedUrl.toString();
}

export function parseYouTubeVideoUrl(input: string): YouTubeVideoEmbed | null {
  const rawInput = input.trim();
  const iframeSrc = extractIframeSrc(rawInput);
  const rawUrl = decodeHtmlEntities(iframeSrc !== null ? iframeSrc : rawInput);
  if (!rawUrl) return null;

  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
  let videoId: string | null = null;

  if (hostname === "youtu.be") {
    videoId = cleanVideoId(url.pathname.split("/").filter(Boolean)[0]);
  }

  if (hostname === "youtube.com" || hostname === "m.youtube.com" || hostname === "music.youtube.com" || hostname === "youtube-nocookie.com") {
    const pathParts = url.pathname.split("/").filter(Boolean);
    if (pathParts[0] === "watch") {
      videoId = cleanVideoId(url.searchParams.get("v"));
    }
    if (pathParts[0] === "embed") {
      videoId = cleanVideoId(pathParts[1]);
    }
    if (pathParts[0] === "shorts") {
      videoId = cleanVideoId(pathParts[1]);
    }
    if (pathParts[0] === "live") {
      videoId = cleanVideoId(pathParts[1]);
    }
  }

  if (!videoId) return null;

  return {
    provider: "youtube",
    videoId,
    url: youtubeEmbedUrl(videoId, url.toString()),
    embedUrl: youtubeEmbedUrl(videoId, url.toString()),
  };
}

export function youtubeNoCookieEmbedUrl(videoId: string) {
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}
